import {
  AddFilterSVG,
  RemoveFilterSVG,
} from '@modules/common/components/SVGIcons';
import { isEmptyWhereClause } from '@modules/common/utils';
import { DashboardQueryBuilderV2 } from '@modules/dashboard/components/GlobalFilters/DashboardQueryBuilderV2';
import { DurationField } from '@modules/dashboard/components/GlobalFilters/DurationField';
import { GlobalFilterActions } from '@modules/dashboard/components/GlobalFilters/GlobalFilterActions';
import {
  getGlobalFilterCount,
  trackDashboardGlobalFilterEvents,
} from '@modules/dashboard/helpers';
import { DashboardActionType } from '@modules/dashboard/store/dashboardActionType';
import { useDashboardStore } from '@modules/dashboard/store/useDashboardStore';
import { getReportSourceLink } from '@modules/report/helpers';
import {
  Avatar,
  Box,
  Button,
  Collapse,
  Link,
  Paper,
  Stack,
  Typography,
  alpha,
} from '@mui/material';
import pluralize from 'pluralize';
import { useEffect } from 'react';
import { useFromWhereClause } from 'v2/core/services/queryBuilder/useFromWhereClause';

interface GlobalFiltersProps {
  isReadOnly: boolean;
  shouldHideWhereFilter: boolean;
  shouldHideSaveToDashboardButton?: boolean;
  shouldAutoFocusFilterInput?: boolean;
}

export const GlobalFilters = ({
  isReadOnly = false,
  shouldHideWhereFilter = false,
  shouldHideSaveToDashboardButton = false,
  shouldAutoFocusFilterInput = true,
}: GlobalFiltersProps) => {
  const fromWhereClause = useFromWhereClause();
  const query = useDashboardStore(({ query }) => query);

  const dashboardLink = getReportSourceLink();
  const numberOfWhereFilter = getGlobalFilterCount({
    nodes: fromWhereClause(query.whereClause),
  });
  const hasDefaultDuration = !query.duration;
  const hasNoFilters = !numberOfWhereFilter && hasDefaultDuration;
  const queryBuilderKey = useDashboardStore(
    ({ visual }) => visual.queryBuilderKey,
  );
  const shouldShowQueryBuilder = useDashboardStore(
    ({ visual }) => visual.shouldShowQueryBuilder,
  );
  const dispatch = useDashboardStore(({ dispatch }) => dispatch);

  const shouldShowFilterCount = !shouldShowQueryBuilder && numberOfWhereFilter;

  useEffect(() => {
    if (isReadOnly && !shouldShowQueryBuilder) {
      dispatch({
        type: DashboardActionType.SET_VISUAL,
        payload: {
          shouldShowQueryBuilder: true,
        },
      });
    }
  }, [dispatch, isReadOnly, shouldShowQueryBuilder]);

  if (isReadOnly && hasNoFilters) {
    return null;
  }

  const toggleQueryBuilderVisibility = () => {
    dispatch({
      type: DashboardActionType.SET_VISUAL,
      payload: {
        shouldShowQueryBuilder: !shouldShowQueryBuilder,
      },
    });
    const eventName = shouldShowQueryBuilder
      ? 'Dashboard Global Filter - Hide Filter Clicked'
      : numberOfWhereFilter
      ? 'Dashboard Global Filter - Show Filter Clicked'
      : 'Dashboard Global Filter - Add Filter Clicked';
    trackDashboardGlobalFilterEvents({
      eventName,
      route: window.location.pathname,
    });
  };

  const areGlobalFiltersApplied =
    !!query.duration || !isEmptyWhereClause(query.whereClause);

  const endIcon = shouldShowFilterCount ? (
    <Avatar
      sx={{
        bgcolor: areGlobalFiltersApplied ? 'info.main' : 'primary.main',
        width: 20,
        height: 20,
        mr: 1,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: areGlobalFiltersApplied
            ? 'info.contrastText'
            : 'primary.contrastText',
          fontWeight: 'bold',
        }}
      >
        {numberOfWhereFilter}
      </Typography>
    </Avatar>
  ) : undefined;

  const getButtonLabel = () => {
    const hiddenFilterButtonLabel = numberOfWhereFilter
      ? 'Show Filters'
      : 'Add Filter';

    return shouldShowQueryBuilder ? 'Hide Filter' : hiddenFilterButtonLabel;
  };

  return (
    <Paper
      sx={{
        px: 3,
        py: 2,
        borderColor: (theme) =>
          areGlobalFiltersApplied
            ? alpha(theme.palette.info.main, 0.3)
            : undefined,
        bgcolor: (theme) =>
          areGlobalFiltersApplied
            ? alpha(theme.palette.info.main, 0.05)
            : undefined,
      }}
      variant="outlined"
    >
      <Stack>
        <Stack direction="row" justifyContent="space-between">
          <Stack direction="row" spacing={1}>
            <DurationField
              isReadOnly={isReadOnly}
              buttonColor={areGlobalFiltersApplied ? 'info' : 'primary'}
            />
            {!shouldHideWhereFilter && (
              <Button
                color={areGlobalFiltersApplied ? 'info' : 'primary'}
                disabled={isReadOnly}
                startIcon={
                  shouldShowQueryBuilder ? (
                    <RemoveFilterSVG fill="currentColor" />
                  ) : (
                    <AddFilterSVG fill="currentColor" />
                  )
                }
                endIcon={endIcon}
                variant={'text'}
                size="small"
                onClick={toggleQueryBuilderVisibility}
                sx={{
                  whiteSpace: 'nowrap',
                }}
                data-testid="dashboard-filters-toggle-button"
              >
                {getButtonLabel()}
              </Button>
            )}
          </Stack>
          <Stack justifyContent="center">
            {shouldHideWhereFilter && numberOfWhereFilter > 0 && (
              <Link target="_blank" href={dashboardLink}>
                {numberOfWhereFilter} {pluralize('filter', numberOfWhereFilter)}{' '}
                applied
              </Link>
            )}
            {!isReadOnly && !shouldHideWhereFilter && (
              <GlobalFilterActions
                isQueryBuilderVisible={shouldShowQueryBuilder}
                shouldHideSaveToDashboardButton={
                  shouldHideSaveToDashboardButton
                }
                buttonColor={areGlobalFiltersApplied ? 'info' : 'primary'}
              />
            )}
          </Stack>
        </Stack>
        {!shouldHideWhereFilter && (
          <Collapse in={shouldShowQueryBuilder} component={Box}>
            <DashboardQueryBuilderV2
              key={queryBuilderKey}
              shouldSkipRender={!shouldShowQueryBuilder}
              areGlobalFiltersApplied={areGlobalFiltersApplied}
              shouldAutoFocus={shouldAutoFocusFilterInput}
            />
          </Collapse>
        )}
      </Stack>
    </Paper>
  );
};
